import { Modal } from '../../../../components/Modal/Modal'
import React, { useEffect, useState } from 'react'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useGetIntegrationUsersQuery, useLazyGetIntegrationUsersQuery, useSaveNoteMutation } from '../../../../store/integrations/integrations'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import 'react-datepicker/dist/react-datepicker.css'
import { addToast } from '../../../../store/toast/toastSlice'
import './AddToCRMModal.scss'
import { getContactOptions } from '../utils'
import { useParams } from 'react-router-dom'
import { useExportConfirmation } from '../../../../components/ConfirmationDialog/useExportConfirmation'
import { UserSelectDropdown } from '../UserDropdown'
import { TeamSelectDropdown } from '../TeamDropdown'
import { ContactDropdown } from '../ContactDropdown'
import { IntegrationServiceId } from '../../../../common/integrations'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

type FormValues = {
  externalUserId: string
  notifyUserId: string
  notifyTeamId: string
}
export interface AddNoteToCRMModalProps {
  note: string
  serviceId: IntegrationServiceId
  show: boolean
  onHide: () => void
}

export const AddNoteToCRMModal: React.FC<AddNoteToCRMModalProps> = ({ show, onHide, note, serviceId }) => {
  const _useAppDispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation()
  const { roomId = '', sessionId = '' } = useParams()

  const { tracked } = useTracking()
  const [triggerSaveNote, { isSuccess, isLoading }] = useSaveNoteMutation()

  const defaultValues = {
    externalUserId: '',
  }

  const handleOnHide = () => {
    onHide()
  }

  const handleSave = tracked(
    async (data: FormValues) => {
      await runMutationWithConfirmation(async () => {
        await triggerSaveNote({
          accountId,
          connector: serviceId,
          roomId: roomId,
          sessionId: sessionId,
          relatedTo: {
            id: data.externalUserId,
          },
          ...(data.notifyUserId ? { notifyUserId: data.notifyUserId } : {}),
          ...(data.notifyTeamId ? { notifyTeamId: data.notifyTeamId } : {}),
          body: {
            note: note,
          },
        })
      })
    },
    { eventName: TrackEvent.SAVE_RESULTS_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          note: note,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  useEffect(() => {
    if (isSuccess) {
      handleOnHide()
      form.reset()
      _useAppDispatch(addToast({ variant: 'success', title: 'Results saved', messages: ['Results were successfully saved to your CRM'] }))
    }
  }, [form, isSuccess, onHide, _useAppDispatch])

  return (
    <>
      <Modal
        show={show}
        onHide={handleOnHide}
        header={
          <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
            Save your notes to&nbsp;<span className='text-capitalize'>{serviceId}</span>
          </BootstrapModal.Title>
        }
        body={
          <FormProvider {...form}>
            <AddNoteToCRMModalBody note={note} serviceId={serviceId} />
          </FormProvider>
        }
        showFooterActions={true}
        onFooterPrimaryAction={handleSubmit(handleSave)}
        footerPrimaryActionLoading={isLoading || accountIsLoading}
        onFooterSecondaryAction={handleOnHide}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

const AddNoteToCRMModalBody: React.FC<{
  note: string
  serviceId: IntegrationServiceId
}> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })

  const { control, setValue, watch } = useFormContext<FormValues>()
  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    if (contacts && contacts.data) {
      setContactOptions(getContactOptions(contacts.data, serviceId))
    }
  }, [contacts])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({ accountId, connector: serviceId, filters: { name: searchInput } }).unwrap()
      if (contactsData && contactsData.data) {
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (isContactsFetching && !contacts) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactDropdown fieldName='externalUserId' contactOptions={contactOptions} serviceId={serviceId} onSearchInputChange={onSearchInputChange} />
        </Col>
      </Row>
      {serviceId === 'redtail' && (
        <div>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify user</div>
              <Controller
                name='notifyUserId'
                control={control}
                render={({ field }) => <UserSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify team</div>
              <Controller
                name='notifyTeamId'
                control={control}
                render={({ field }) => <TeamSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
        </div>
      )}
    </Container>
  )
}
